import React, { useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';

import type { OpportunityRelatedInclusion } from '~/hooks/apis/offers/useGetOpportunityRelated';

import InclusionDetailField from './InclusionDetailField';
import TruncatedCellText from './TruncatedCellText';
import { INCLUSION_NAME_COLUMN_MIN_WIDTH } from './constants';
import { formatDealOptionCurrency, formatDealOptionValue } from './dealOptionsFormatters';

interface Props {
  inclusions: Array<OpportunityRelatedInclusion>;
}

export default function InclusionsPanel({ inclusions }: Props) {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const toggleRow = (rowId: string) => {
    setExpandedRows((currentRows) => ({ ...currentRows, [rowId]: !currentRows[rowId] }));
  };

  return (
    <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ minWidth: INCLUSION_NAME_COLUMN_MIN_WIDTH }}>Inclusion Name</TableCell>
          <TableCell>Inclusion Description</TableCell>
          <TableCell align="right" sx={{ width: 100 }}>
            RRP
          </TableCell>
          <TableCell sx={{ width: 110 }}>Applies For</TableCell>
          <TableCell sx={{ width: 48 }} />
        </TableRow>
      </TableHead>
      <TableBody>
        {inclusions.map((inclusion) => {
          const isRowExpanded = !!expandedRows[inclusion.id];
          const name = formatDealOptionValue(inclusion.name);
          const description = formatDealOptionValue(inclusion.inclusion_description__c ?? inclusion.description__c);

          return (
            <React.Fragment key={inclusion.id}>
              <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleRow(inclusion.id)}>
                <TableCell sx={{ minWidth: INCLUSION_NAME_COLUMN_MIN_WIDTH }}>
                  <TruncatedCellText title={name}>{name}</TruncatedCellText>
                </TableCell>
                <TableCell>
                  <TruncatedCellText title={description}>{description}</TruncatedCellText>
                </TableCell>
                <TableCell align="right">{formatDealOptionCurrency('AUD', inclusion.rrp__c)}</TableCell>
                <TableCell>{formatDealOptionValue(inclusion.type__c)}</TableCell>
                <TableCell>
                  <IconButton size="small" aria-label={isRowExpanded ? 'Hide details' : 'Show details'}>
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{ transform: isRowExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
                    />
                  </IconButton>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={5} sx={{ borderBottom: isRowExpanded ? 1 : 0, borderColor: 'divider', p: 0 }}>
                  <Collapse unmountOnExit in={isRowExpanded} timeout="auto">
                    <Box
                      sx={{ bgcolor: 'action.hover', borderLeft: 4, borderLeftColor: 'divider', pl: 2, pr: 3, py: 1.5 }}
                    >
                      <Stack spacing={1.5}>
                        <InclusionDetailField label="Inclusion name" value={name} />
                        <InclusionDetailField label="Inclusion description" value={description} />
                      </Stack>
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}
